import { useEffect, useRef, useState } from 'react';
import {
  Anchor,
  Button,
  Card,
  Grid,
  Group,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useFocusTrap } from '@mantine/hooks';
import { IconAt } from '@tabler/icons-react';
import logo from 'images/large/logo_colors_black.png';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { signIn } from 'next-auth/react';

import { Error } from 'components/auth/Error';
import { SupportLink } from 'components/ui/SupportLink';
import { defaultToastNotification, toast } from 'components/utils/toast';
import { useOrganizationCreateTrialUser } from 'utils/api/organization/use-organization-create-trial-user';
import { useCheckDomain } from 'utils/api/saml/use-check-domain';
import { normalizeEmail } from 'utils/email';

import classes from './EmailLogin.module.css';

interface EmailLoginProps {
  type: 'login' | 'signup' | 'trial';
}

export const EmailLogin = ({ type }: EmailLoginProps) => {
  const router = useRouter();
  const focusTrapRef = useFocusTrap();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { mutate: createOrgAndUser } = useOrganizationCreateTrialUser({
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') {
        toast.error('A user already exists with this email, try logging in');
      } else {
        defaultToastNotification();
      }

      setIsLoading(false);
    },
    onSuccess: (email) => {
      void signIn('email', {
        email,
        redirect: false,
      })
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          defaultToastNotification();
          setIsLoading(false);
        });
    },
  });

  const { mutate: checkDomain } = useCheckDomain({
    onSuccess: (data, { email }) => {
      if (data.ssoEnabled) {
        window.location.href = `/api/auth/saml/authorize?email=${encodeURIComponent(email)}`;
        return;
      }
      // No SSO -- proceed with email sign-in
      void signIn('email', {
        email,
        redirect: false,
      })
        .then((res) => {
          if (res?.ok) {
            void router.push(
              `/auth/verify-request?email=${encodeURIComponent(email)}`,
            );
          } else {
            defaultToastNotification();
          }
        })
        .catch(() => {
          defaultToastNotification();
          setIsLoading(false);
        });
    },
    onError: (_err, { email }) => {
      // If domain check fails, fall through to normal email flow
      void signIn('email', {
        email,
        redirect: false,
      })
        .then((res) => {
          if (res?.ok) {
            void router.push(
              `/auth/verify-request?email=${encodeURIComponent(email)}`,
            );
          } else {
            defaultToastNotification();
          }
        })
        .catch(() => {
          defaultToastNotification();
          setIsLoading(false);
        });
    },
  });

  const searchParams = useSearchParams();
  const error = searchParams?.get('error');
  const email = searchParams?.get('email') || '';

  const form = useForm({
    initialValues: {
      email,
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
    },
  });

  const isTrial = type === 'trial';
  const isSigningUp = type === 'signup';
  const isLogginIn = !isTrial && !isSigningUp;

  useEffect(() => {
    if (email) {
      form.setValues({ email });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  if (error) {
    return <Error error={error} />;
  }

  return (
    <Card withBorder p={24} ref={focusTrapRef}>
      <form
        onSubmit={form.onSubmit((values) => {
          const normalizedEmail = normalizeEmail(values.email);
          setIsLoading(true);
          if (isTrial) {
            createOrgAndUser({ email: normalizedEmail });
          } else {
            checkDomain({ email: normalizedEmail });
          }
        })}
      >
        <Grid gutter={16} justify="center">
          <Grid.Col span={12}>
            <div className={classes.wrapper}>
              <Image
                src={logo}
                alt="BiggerPicture"
                style={{
                  objectFit: 'contain',
                  padding: 'var(--logo-padding)',
                }}
                fill
              />
            </div>
          </Grid.Col>
          <Grid.Col span={12}>
            {isTrial && (
              <>
                <Text mb="sm">
                  {
                    "Try the BiggerPicture Appointment Scheduling Network. Once you receive a login code in your inbox, you'll be able to sign in and start your warehouse trial."
                  }
                </Text>
                <Text>{`Enter your email address to sign up:`}</Text>
              </>
            )}
            {isSigningUp && (
              <>
                <Text mb="sm">
                  {
                    "Join the BiggerPicture Appointment Scheduling Network. Once you receive a login code in your inbox, you'll be able to choose whether to sign up as a carrier/broker, facility, or customer."
                  }
                </Text>
                <Text>{`Enter your email address to sign up:`}</Text>
              </>
            )}
            {isLogginIn && (
              <Text>{`Enter your email address to sign in:`}</Text>
            )}
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              name="email"
              ref={inputRef}
              data-autofocus
              leftSection={<IconAt size={18} />}
              placeholder="Your email"
              type="email"
              required
              {...form.getInputProps('email')}
            />
          </Grid.Col>
          <Grid.Col span={12} />
          <Grid.Col>
            <Group justify="center">
              <Button
                type="submit"
                loading={isLoading}
                disabled={
                  form.values.email.length < 1 ||
                  !inputRef.current?.checkValidity()
                }
              >
                {isSigningUp ? 'Verify email address' : 'Sign in with email'}
              </Button>
            </Group>
          </Grid.Col>
          <Grid.Col span={12}>
            <Grid gutter={10} style={{ textAlign: 'center' }}>
              <Grid.Col mt={20}>
                {isSigningUp ? (
                  <Text>
                    {'Already have an account? '}
                    <Anchor component={Link} href="/auth/signin">
                      Sign in
                    </Anchor>
                  </Text>
                ) : (
                  <Text>
                    {'Need an account? '}
                    <Anchor component={Link} href="/signup">
                      Sign up
                    </Anchor>
                  </Text>
                )}
              </Grid.Col>
              <Grid.Col span={12} />
              <Grid.Col>
                <SupportLink />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </form>
    </Card>
  );
};
